import { SERVER_URL } from "../../../constants";

function getAuthHeaders() {
  const token = sessionStorage.getItem("bearer");

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse(response, fallbackMessage) {
  if (response.ok) {
    if (response.status === 204) return null;
    return response.json();
  }

  let message = fallbackMessage;
  let body = null;

  try {
    const text = await response.text();
    if (text) {
      try {
        body = JSON.parse(text);
      } catch (_) {
        // plain-text body
      }
      // Structured errors carry a human message and a code (e.g. STALE_MEMBERS);
      // show the message rather than the raw JSON.
      message = (body && body.message) || text;
    }
  } catch (e) {
    // ignore read errors
  }

  const error = new Error(message);
  error.status = response.status;
  error.code = body && body.code ? body.code : undefined;
  error.body = body;
  throw error;
}

export async function getActivityGroupsByDate(date, campus) {
  const params = new URLSearchParams({ date });
  // Scope to a single campus when one is in focus so the backend doesn't ship
  // (and the client doesn't filter out) every other campus's groups.
  if (campus) params.set("campus", campus);

  const response = await fetch(
    `${SERVER_URL}api/activities/groups?${params.toString()}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  return handleResponse(response, "Failed to load activity groups");
}

export async function getActivityCandidates({ date, slot, campus }) {
  const params = new URLSearchParams({ date, slot });
  if (campus) params.set("campus", campus);

  const response = await fetch(
    `${SERVER_URL}api/activities/candidates?${params.toString()}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  return handleResponse(response, "Failed to load activity candidates");
}

export async function getActivityLeaders(date, campus) {
  const response = await fetch(
    `${SERVER_URL}api/activities/leaders?date=${encodeURIComponent(date)}&campus=${encodeURIComponent(campus)}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  return handleResponse(response, "Failed to load activity leaders");
}

export async function getActivityProducts(date) {
  const response = await fetch(
    `${SERVER_URL}api/activities/products?date=${encodeURIComponent(date)}`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );

  return handleResponse(response, "Failed to load activity products");
}

export async function saveActivityGroup({ mode, id, payload }) {
  if (mode === "add") {
    const response = await fetch(`${SERVER_URL}api/activities/groups`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    return handleResponse(response, "Failed to create activity group");
  }

  const response = await fetch(`${SERVER_URL}api/activities/groups/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleResponse(response, "Failed to update activity group");
}

export async function copyActivityGroups(payload) {
  const response = await fetch(`${SERVER_URL}api/activities/groups/copy`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleResponse(response, "Failed to copy activity groups");
}

export async function updateActivityGroupMembers(groupId, members) {
  const response = await fetch(
    `${SERVER_URL}api/activities/groups/${groupId}/members`,
    {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(members),
    }
  );

  return handleResponse(response, "Failed to update activity group members");
}

export async function deleteActivityGroup(groupId) {
  const response = await fetch(`${SERVER_URL}api/activities/groups/${groupId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  return handleResponse(response, "Failed to delete activity group");
}